import { useEffect, useState, useCallback } from "react";
import { Plus, PencilSimple, Trash, Clock } from "@phosphor-icons/react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import Modal, { Field, inputCls, PrimaryBtn, GhostBtn } from "@/components/Modal";
import { toast } from "sonner";

const CATEGORIES = ["Hair", "Barber", "Color", "Treatment", "Nails", "Spa"];
const empty = { name: "", category: "Hair", duration_min: 30, price: 0, description: "", active: true };

export default function Services() {
  const { currency } = useAuth();
  const { t } = useI18n();
  const [services, setServices] = useState([]);
  const [dialog, setDialog] = useState({ open: false, id: null });
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/services");
    setServices(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setDialog({ open: true, id: null }); };
  const openEdit = (s) => { setForm({ name: s.name, category: s.category, duration_min: s.duration_min, price: s.price, description: s.description || "", active: s.active !== false }); setDialog({ open: true, id: s.id }); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, duration_min: Number(form.duration_min), price: Number(form.price) };
      if (dialog.id) await api.put(`/services/${dialog.id}`, payload);
      else await api.post("/services", payload);
      toast.success(dialog.id ? t.svc.updated : t.svc.added);
      setDialog({ open: false, id: null });
      load();
    } catch (e) { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/services/${id}`); toast.success(t.svc.removed); load(); };

  return (
    <div className="fade-up space-y-6" data-testid="services-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="label-caps">{t.svc.overline}</p>
          <h1 className="font-heading text-3xl font-light tracking-tight mt-1">{t.svc.title}</h1>
        </div>
        <button data-testid="new-service-btn" onClick={openNew}
          className="px-4 py-2.5 rounded-lg bg-[#2C3E35] text-white text-sm font-medium hover:bg-[#1E2B25] transition-colors inline-flex items-center gap-2">
          <Plus size={16} weight="bold" /> {t.svc.add}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => (
          <div key={s.id} data-testid={`service-card-${s.id}`} className="bg-white rounded-xl border border-[#E5E2DC] p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm group">
            <div className="flex items-start justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#2C3E35]/8 text-[#2C3E35]">{s.category}</span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button data-testid={`edit-service-${s.id}`} onClick={() => openEdit(s)} className="p-1.5 rounded-lg text-[#6B6A67] hover:bg-[#E8E4DF]"><PencilSimple size={15} /></button>
                <button data-testid={`del-service-${s.id}`} onClick={() => del(s.id)} className="p-1.5 rounded-lg text-[#6B6A67] hover:bg-[#B86B5A]/12 hover:text-[#B86B5A]"><Trash size={15} /></button>
              </div>
            </div>
            <h3 className="font-heading text-lg font-medium mt-3">{s.name}</h3>
            {s.description && <p className="text-xs text-[#6B6A67] mt-1 leading-relaxed">{s.description}</p>}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E5E2DC]">
              <span className="flex items-center gap-1.5 text-xs text-[#6B6A67]"><Clock size={14} /> {s.duration_min} {t.svc.min}</span>
              <span className="font-heading text-lg font-semibold">{money(s.price, currency)}</span>
            </div>
          </div>
        ))}
        {services.length === 0 && <div className="col-span-full py-16 text-center text-[#6B6A67] text-sm">{t.svc.none}</div>}
      </div>

      <Modal open={dialog.open} onClose={() => setDialog({ open: false, id: null })} testid="service-dialog"
        title={dialog.id ? t.svc.edit : t.svc.addTitle}
        footer={<><GhostBtn onClick={() => setDialog({ open: false, id: null })}>{t.btn.cancel}</GhostBtn><PrimaryBtn onClick={save} disabled={saving} data-testid="service-save">{saving ? t.btn.saving : t.btn.save}</PrimaryBtn></>}>
        <div className="space-y-4">
          <Field label={t.svc.fName}><input data-testid="service-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Signature Haircut" /></Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label={t.svc.fCategory}>
              <select data-testid="service-category" className={inputCls} value={form.category} onChange={set("category")}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t.svc.fDuration}><input data-testid="service-duration" type="number" className={inputCls} value={form.duration_min} onChange={set("duration_min")} /></Field>
            <Field label={t.svc.fPrice}><input data-testid="service-price" type="number" className={inputCls} value={form.price} onChange={set("price")} /></Field>
          </div>
          <Field label={t.svc.fDescription}><textarea data-testid="service-description" className={inputCls} rows={2} value={form.description} onChange={set("description")} /></Field>
        </div>
      </Modal>
    </div>
  );
}
